import React, { FC, useState } from 'react';
import { Modal } from 'antd';
import { ReactComponent as GoogleIcon } from 'src/assets/images/onboarding-trainer/google.svg';
import { ReactComponent as AppleIcon } from 'src/assets/images/onboarding-trainer/apple.svg';
import { ConnectedCalendarInfo } from 'src/api/queries/useGetTrainerSchedule';
import toast from 'src/utils/appAlert';
import './ConnectCalendarModal.scss';

interface ConnectCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  connectedCalendar?: ConnectedCalendarInfo | null;
  onSaveCalendar: (calendarInfo: ConnectedCalendarInfo) => Promise<void>;
  isSaving?: boolean;
}

const modalStyles = {
  mask: {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(4, 5, 27, 0.75)',
  },
  container: {
    background: '#0c0f2b',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: '20px',
    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.6)',
    padding: 0,
    color: '#ffffff',
    overflow: 'hidden',
  },
  body: {
    padding: 0,
    background: 'transparent',
  },
};

export const ConnectCalendarModal: FC<ConnectCalendarModalProps> = ({
  isOpen,
  onClose,
  connectedCalendar,
  onSaveCalendar,
  isSaving = false,
}) => {
  const [activeProvider, setActiveProvider] = useState<'google' | 'apple' | null>(
    connectedCalendar?.connected ? connectedCalendar.provider : null,
  );
  const [connecting, setConnecting] = useState<'google' | 'apple' | null>(null);

  const handleSelectProvider = async (provider: 'google' | 'apple') => {
    if (isSaving || connecting) return;

    setConnecting(provider);
    try {
      if (activeProvider === provider) {
        // Disconnect
        await onSaveCalendar({
          provider: null,
          connected: false,
        });
        setActiveProvider(null);
        toast.success(
          `${provider === 'google' ? 'Google' : 'Apple'} Calendar disconnected successfully.`,
        );
      } else {
        // Connect
        await onSaveCalendar({
          provider,
          connected: true,
          email: provider === 'google' ? 'trainer@gmail.com' : 'trainer@icloud.com',
        });
        setActiveProvider(provider);
        toast.success(
          `${provider === 'google' ? 'Google' : 'Apple'} Calendar connected successfully.`,
        );
        onClose();
      }
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to update calendar connection';
      toast.error(errorMsg);
    } finally {
      setConnecting(null);
    }
  };

  return (
    <Modal
      open={isOpen}
      footer={null}
      onCancel={onClose}
      centered
      width={480}
      closeIcon={null}
      rootClassName="connect-calendar-modal-root"
      className="connect-calendar-modal"
      styles={modalStyles}
    >
      <div className="connect-calendar-modal-inner">
        {/* Header */}
        <div className="modal-header-row">
          <div className="header-titles">
            <h2 className="modal-title">{'Connect your calendar'}</h2>
            <p className="modal-subtitle">{'Choose the account where your calendar is stored.'}</p>
          </div>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Close">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Providers List */}
        <div className="calendar-options-list">
          {/* Google Calendar */}
          <div
            className={`calendar-option-card ${activeProvider === 'google' ? 'connected' : ''}`}
            onClick={() => handleSelectProvider('google')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleSelectProvider('google')}
          >
            <div className="provider-icon-wrapper google">
              <GoogleIcon />
            </div>
            <div className="provider-info">
              <span className="provider-title">{'Google Calendar'}</span>
              <span className="provider-desc">{'Connect your Google Account'}</span>
            </div>
            <div className="provider-action-state">
              {connecting === 'google' ? (
                <span className="connecting-spinner">{'Connecting...'}</span>
              ) : activeProvider === 'google' ? (
                <span className="badge-connected">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {'Connected'}
                </span>
              ) : (
                <svg
                  className="arrow-chevron"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              )}
            </div>
          </div>

          {/* Apple Calendar (iCloud) */}
          <div
            className={`calendar-option-card ${activeProvider === 'apple' ? 'connected' : ''}`}
            onClick={() => handleSelectProvider('apple')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleSelectProvider('apple')}
          >
            <div className="provider-icon-wrapper apple">
              <AppleIcon />
            </div>
            <div className="provider-info">
              <span className="provider-title">{'Apple Calendar (iCloud)'}</span>
              <span className="provider-desc">{'Connect calendars stored in iCloud'}</span>
            </div>
            <div className="provider-action-state">
              {connecting === 'apple' ? (
                <span className="connecting-spinner">{'Connecting...'}</span>
              ) : activeProvider === 'apple' ? (
                <span className="badge-connected">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {'Connected'}
                </span>
              ) : (
                <svg
                  className="arrow-chevron"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
